import type { ExportFormat } from '../exports/types';

type AnyMap = Record<string, any>;

/**
 * Campos de metadados da API que NÃO devem ir para o CorelDRAW
 * Estes campos são da tabela de perfis/configurações, não são opções de exportação
 */
const METADATA_FIELDS = new Set([
  'id',
  'profile_id',
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
  'deleted_at',
  'version',
  'tenant_id',
]);

/**
 * Remove campos de metadados da API que não são opções de exportação
 * ⚠️ CRÍTICO para HMAC: Backend C# não inclui esses campos na canonicalização
 */
function cleanMetadata(overrides: AnyMap): AnyMap {
  const cleaned: AnyMap = {};
  
  for (const [key, value] of Object.entries(overrides)) {
    // Não incluir campos de metadados
    if (METADATA_FIELDS.has(key)) {
      continue;
    }
    cleaned[key] = value;
  }
  
  return cleaned;
}

/**
 * Normaliza DPI: se vier "dpi" genérico, expande para dpix/dpiy
 * Apenas normalização, sem adicionar defaults
 */
function normalizeDpi(overrides: AnyMap): AnyMap {
  const out: AnyMap = { ...overrides };
  if (typeof out.dpi === 'number' && (!out.dpix || !out.dpiy)) {
    if (!out.dpix) out.dpix = out.dpi;
    if (!out.dpiy) out.dpiy = out.dpi;
  }
  if ('dpi' in out) delete out.dpi;
  return out;
}

/**
 * Build PNG: Passa dados da API sem modificação
 * Remove defaults - API deve fornecer todos os campos necessários
 */
function buildPng(base: AnyMap): { ok: true; value: AnyMap } {
  const o = normalizeDpi(base);
  const cleaned = cleanMetadata(o); // ✅ Filtrar metadados da API
  
  return { ok: true, value: cleaned };
}

/**
 * Build TIFF: Passa dados da API sem modificação
 * Remove defaults - API deve fornecer todos os campos necessários
 */
function buildTiff(base: AnyMap): { ok: true; value: AnyMap } {
  const o = normalizeDpi(base);
  const cleaned = cleanMetadata(o); // ✅ Filtrar metadados da API
  
  return { ok: true, value: cleaned };
}

/**
 * Aliases PDF: nomes antigos do frontend → nomes corretos do BD
 */
const PDF_ALIASES: Record<string, string> = {
  jpeg_quality: 'jpeg_quality_int',
  compress_text_and_graphics: 'compress_text_graphics',
  pdf_preset: 'preset',
};

/**
 * Aliases EPS: nomes antigos do frontend → nomes corretos do BD
 */
const EPS_ALIASES: Record<string, string> = {
  embed_fonts: 'include_fonts',
  preview_type: 'preview',
  bleed_limit: 'bleed_limit_mm',
};

/**
 * Normaliza aliases: renomeia chaves antigas para nomes corretos do BD.
 * Se ambas as chaves existirem, o nome correto (BD) prevalece.
 */
function normalizeAliases(overrides: AnyMap, aliases: Record<string, string>): AnyMap {
  const out: AnyMap = { ...overrides };
  for (const [oldKey, newKey] of Object.entries(aliases)) {
    if (oldKey in out && !(newKey in out)) {
      out[newKey] = out[oldKey];
      delete out[oldKey];
    } else if (oldKey in out && newKey in out) {
      // BD name already exists, just remove the old alias
      delete out[oldKey];
    }
  }
  return out;
}

/**
 * Build PDF: Normaliza aliases e remove metadados
 */
function buildPdf(base: AnyMap): { ok: true; value: AnyMap } {
  const aliased = normalizeAliases(base, PDF_ALIASES);
  const cleaned = cleanMetadata(aliased);
  return { ok: true, value: cleaned };
}

/**
 * Build EPS: Normaliza aliases, DPI e remove metadados
 */
function buildEps(base: AnyMap): { ok: true; value: AnyMap } {
  const aliased = normalizeAliases(base, EPS_ALIASES);
  const withDpi = normalizeDpi(aliased);
  const cleaned = cleanMetadata(withDpi);
  return { ok: true, value: cleaned };
}

/**
 * Constrói overrides para o formato especificado
 * NOVO COMPORTAMENTO:
 * - Prioriza profileOptions (dados da API) - 100%
 * - formOptions apenas complementam (raramente usado)
 * - SEM DEFAULTS - API deve fornecer tudo
 * - SEM VALIDAÇÃO - CorelDRAW valida campos obrigatórios
 */
export function buildOverridesForFormat(
  format: ExportFormat,
  profileOptions: AnyMap | null | undefined,
  formOptions: AnyMap | null | undefined
): { ok: true; value: AnyMap } | { ok: false; error: string } {
  // API prevalece, formOptions apenas complementam
  const base: AnyMap = { ...(formOptions || {}), ...(profileOptions || {}) };
  
  switch (format) {
    case 'png':
      return buildPng(base);
    case 'tiff':
      return buildTiff(base);
    case 'pdf':
      return buildPdf(base);
    case 'eps':
      return buildEps(base);
    default:
      // Para outros formatos, retornar dados puros
      return { ok: true, value: base };
  }
}
